import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  GetApp as ExportIcon,
  MoreVert as MoreIcon,
  Description as DocumentIcon,
  CloudDownload as DownloadIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Attachment as AttachmentIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  setWorkInstructionFilters,
  setWorkInstructionPagination,
  setWorkInstructionBulkSelection,
  setWorkInstructionDialogOpen,
  setSelectedWorkInstruction,
  setDocumentPreviewOpen,
} from '../../store/reducers/workInstructionsSlice';
import { workInstructionsApi } from '../../services/workInstructionsApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  searchFilter: {
    marginBottom: theme.spacing(2),
  },
  statisticsCard: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  statusChip: {
    minWidth: 80,
  },
  bulkActions: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    margin: theme.spacing(0.25),
    minWidth: 'auto',
    width: 32,
    height: 32,
  },
  workInstructionName: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  documentChip: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    '&:hover': {
      backgroundColor: '#bbdefb',
    },
  },
  noDocumentChip: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
  },
}));

const WorkInstructionsList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    workInstructions: {
      items: workInstructions,
      loading,
      pagination,
      filters,
      statistics,
      bulkSelection,
    },
  } = useSelector((state: RootState) => state.workInstructions);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWorkInstructionId, setSelectedWorkInstructionId] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    dispatch({ type: 'workInstructions/fetchWorkInstructions' });
  }, [dispatch]);

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setWorkInstructionFilters({ search: event.target.value }));
  };

  // Handle filters
  const handleFilterChange = (field: string, value: any) => {
    dispatch(setWorkInstructionFilters({ [field]: value === '' ? undefined : value }));
  };

  // Handle pagination
  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setWorkInstructionPagination({ page: newPage + 1, limit: pagination.limit }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setWorkInstructionPagination({ page: 1, limit: parseInt(event.target.value, 10) }));
  };

  // Handle bulk selection
  const handleBulkSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(setWorkInstructionBulkSelection(workInstructions.map(wi => wi.Id!)));
    } else {
      dispatch(setWorkInstructionBulkSelection([]));
    }
  };

  const handleBulkSelectOne = (workInstructionId: string) => {
    const isSelected = bulkSelection.includes(workInstructionId);
    if (isSelected) {
      dispatch(setWorkInstructionBulkSelection(bulkSelection.filter(id => id !== workInstructionId)));
    } else {
      dispatch(setWorkInstructionBulkSelection([...bulkSelection, workInstructionId]));
    }
  };

  // Handle actions
  const handleCreateWorkInstruction = () => {
    dispatch(setSelectedWorkInstruction(null));
    dispatch(setWorkInstructionDialogOpen(true));
  };

  const handleEditWorkInstruction = (workInstruction: any) => {
    dispatch(setSelectedWorkInstruction(workInstruction));
    dispatch(setWorkInstructionDialogOpen(true));
    setAnchorEl(null);
  };

  const handleDeleteWorkInstruction = (workInstructionId: string) => {
    setSelectedWorkInstructionId(workInstructionId);
    setConfirmDeleteOpen(true);
    setAnchorEl(null);
  };

  const confirmDelete = () => {
    if (selectedWorkInstructionId) {
      dispatch({ type: 'workInstructions/deleteWorkInstruction', payload: selectedWorkInstructionId });
    }
    setConfirmDeleteOpen(false);
    setSelectedWorkInstructionId(null);
  };

  const handleExport = () => {
    dispatch({ type: 'workInstructions/exportWorkInstructions' });
  };

  const handleBulkActivate = () => {
    dispatch({ type: 'workInstructions/bulkActivateWorkInstructions', payload: bulkSelection });
    dispatch(setWorkInstructionBulkSelection([]));
  };

  const handleBulkDeactivate = () => {
    dispatch({ type: 'workInstructions/bulkDeactivateWorkInstructions', payload: bulkSelection });
    dispatch(setWorkInstructionBulkSelection([]));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, workInstructionId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedWorkInstructionId(workInstructionId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedWorkInstructionId(null);
  };

  // Handle document actions
  const handleDownloadDocument = async (workInstruction: any) => {
    try {
      if (workInstruction.Document) {
        await workInstructionsApi.utils.downloadDocumentFile(workInstruction.Document);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
    }
    setAnchorEl(null);
  };

  const handlePreviewDocument = (workInstruction: any) => {
    if (workInstruction.Document) {
      dispatch(setDocumentPreviewOpen({ open: true, document: workInstruction.Document }));
    }
    setAnchorEl(null);
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1">
            Work Instructions Management
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              style={{ marginRight: 8 }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateWorkInstruction}
            >
              Add Work Instruction
            </Button>
          </Box>
        </Box>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} className={classes.statisticsCard}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Work Instructions
                </Typography>
                <Typography variant="h5">
                  {statistics.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active
                </Typography>
                <Typography variant="h5" style={{ color: '#4caf50' }}>
                  {statistics.active}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  With Documents
                </Typography>
                <Typography variant="h5" style={{ color: '#2196f3' }}>
                  {statistics.withDocuments}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Without Documents
                </Typography>
                <Typography variant="h5" style={{ color: '#ff9800' }}>
                  {statistics.withoutDocuments}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Card className={classes.searchFilter}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search work instructions..."
                value={filters.search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon color="action" />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.active === undefined ? '' : filters.active.toString()}
                  onChange={(e) => handleFilterChange('active', e.target.value === '' ? undefined : e.target.value === 'true')}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Document Status</InputLabel>
                <Select
                  value={filters.hasDocument === undefined ? '' : filters.hasDocument.toString()}
                  onChange={(e) => handleFilterChange('hasDocument', e.target.value === '' ? undefined : e.target.value === 'true')}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">With Document</MenuItem>
                  <MenuItem value="false">Without Document</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {bulkSelection.length > 0 && (
        <div className={classes.bulkActions}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">
              {bulkSelection.length} work instruction(s) selected
            </Typography>
            <Box>
              <Button
                size="small"
                startIcon={<ActiveIcon />}
                onClick={handleBulkActivate}
                style={{ marginRight: 8 }}
              >
                Activate
              </Button>
              <Button
                size="small"
                startIcon={<InactiveIcon />}
                onClick={handleBulkDeactivate}
              >
                Deactivate
              </Button>
            </Box>
          </Box>
        </div>
      )}

      {/* Work Instructions Table */}
      <Card>
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={bulkSelection.length > 0 && bulkSelection.length < workInstructions.length}
                    checked={workInstructions.length > 0 && bulkSelection.length === workInstructions.length}
                    onChange={handleBulkSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workInstructions.map((workInstruction) => (
                <TableRow key={workInstruction.Id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={bulkSelection.includes(workInstruction.Id!)}
                      onChange={() => handleBulkSelectOne(workInstruction.Id!)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.workInstructionName}>
                      {workInstruction.Name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {workInstruction.Description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {workInstruction.Document ? (
                      <Chip
                        size="small"
                        icon={<AttachmentIcon />}
                        label={workInstruction.Document.Name}
                        className={classes.documentChip}
                        clickable
                        onClick={() => handlePreviewDocument(workInstruction)}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="No Document"
                        className={classes.noDocumentChip}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={workInstruction.Active ? 'Active' : 'Inactive'}
                      color={workInstruction.Active ? 'primary' : 'default'}
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {workInstruction.Document && (
                        <Tooltip title="Download Document">
                          <IconButton
                            size="small"
                            className={classes.actionButton}
                            style={{ backgroundColor: '#4caf50', color: 'white' }}
                            onClick={() => handleDownloadDocument(workInstruction)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          style={{ backgroundColor: '#2196f3', color: 'white' }}
                          onClick={(e) => handleMenuOpen(e, workInstruction.Id!)}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.limit}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditWorkInstruction(workInstructions.find(wi => wi.Id === selectedWorkInstructionId))}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        {workInstructions.find(wi => wi.Id === selectedWorkInstructionId)?.Document && (
          <MenuItem onClick={() => handlePreviewDocument(workInstructions.find(wi => wi.Id === selectedWorkInstructionId))}>
            <ListItemIcon>
              <DocumentIcon />
            </ListItemIcon>
            <ListItemText primary="Preview Document" />
          </MenuItem>
        )}
        <MenuItem onClick={() => selectedWorkInstructionId && handleDeleteWorkInstruction(selectedWorkInstructionId)}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this work instruction? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WorkInstructionsList; 